import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import { createFilterOptions } from '@mui/material/useAutocomplete'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { InputField } from 'Components/reusable/InputFields/InputField'
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { convertToDisplayDate } from 'utils/utils'
import { IProfileNote, IProfileNoteTag } from 'types/profileInterfaces'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

const CREATE_TAG_SENTINEL_ID = -1
const tagFilter = createFilterOptions<IProfileNoteTag>()

const MAX_NOTE_DEPTH = 3

interface INoteItemProps {
  note: IProfileNote
  depth: number
  canManageNotes: boolean
  canArchiveNotes: boolean
  isOwnerProfile: boolean
  currentAccountId: number | undefined
  replyDraftByNote: Record<number, string>
  replyVisibleToOwnerByNote: Record<number, boolean>
  replyTagIdsByNote: Record<number, number[]>
  editingNoteId: number | null
  editDraft: string
  onReplyDraftChange: (noteId: number, value: string) => void
  onReplyVisibilityChange: (noteId: number, visibleToOwner: boolean) => void
  onReplyTagIdsChange: (noteId: number, tagIds: number[]) => void
  onReply: (note: IProfileNote) => Promise<void>
  onToggleVisibility: (note: IProfileNote) => Promise<void>
  onToggleArchive: (note: IProfileNote) => Promise<void>
  onEditStart: (note: IProfileNote) => void
  onEditDraftChange: (value: string) => void
  availableTags: IProfileNoteTag[]
  canTagNotes: boolean
  canCreateTagInline: boolean
  editTagIds: number[]
  onEditTagIdsChange: (tagIds: number[]) => void
  onResolveTagIds: (values: Array<string | IProfileNoteTag>) => Promise<number[]>
  onEditSave: (note: IProfileNote) => Promise<void>
  onEditCancel: () => void
  activeReplyComposerNoteId: number | null
  onReplyComposerToggle: (noteId: number | null) => void
}

const NoteItem: React.FC<INoteItemProps> = ({
  note,
  depth,
  canManageNotes,
  canArchiveNotes,
  isOwnerProfile,
  currentAccountId,
  replyDraftByNote,
  replyVisibleToOwnerByNote,
  replyTagIdsByNote,
  editingNoteId,
  editDraft,
  onReplyDraftChange,
  onReplyVisibilityChange,
  onReplyTagIdsChange,
  onReply,
  onToggleVisibility,
  onToggleArchive,
  onEditStart,
  onEditDraftChange,
  availableTags,
  canTagNotes,
  canCreateTagInline,
  editTagIds,
  onEditTagIdsChange,
  onResolveTagIds,
  onEditSave,
  onEditCancel,
  activeReplyComposerNoteId,
  onReplyComposerToggle,
}) => {
  const { t } = useTranslation()
  const [isThreadOpen, setIsThreadOpen] = useState(false)
  const isEditing = editingNoteId === note.id
  const isOwnNote = note.creator.accountId === currentAccountId
  const isRootNote = depth === 0
  const isReplyComposerOpen = activeReplyComposerNoteId === note.id

  const canReply =
    depth < MAX_NOTE_DEPTH &&
    (isOwnerProfile ? note.visibleToOwner && !note.archived : canManageNotes && !note.archived)
  const isOwnerOrphanedReply = isOwnerProfile && depth === 0 && note.parentId !== null
  const showOwnerReceipt = canManageNotes && !isOwnerProfile && note.visibleToOwner
  const canCollapseThread = isRootNote && note.replies.length > 0

  return (
    <Paper
      variant="outlined"
      sx={{
        p: depth === 0 ? 2 : 1.5,
        ml: depth * 2,
        borderColor: note.archived ? 'grey.500' : 'grey.400',
        backgroundColor: note.archived ? 'grey.100' : 'background.paper',
        opacity: note.archived ? 0.75 : 1,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant={depth === 0 ? 'body2' : 'caption'} color="text.secondary">
              {`${convertToDisplayDate(note.createdAt)} - ${note.creator.firstName ?? ''} ${note.creator.lastName ?? ''}`.trim()}
            </Typography>
            {note.tags.length > 0 &&
              note.tags.map(tag => (
                <Chip
                  key={tag.id}
                  size="small"
                  label={tag.name}
                  sx={tag.color ? { backgroundColor: tag.color, color: '#fff' } : undefined}
                />
              ))}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color={note.visibleToOwner ? 'success' : 'default'}
                label={note.visibleToOwner ? t('profile.notes.badgeVisibleToOwner') : t('profile.notes.badgeInternal')}
              />
              {showOwnerReceipt && note.ownerRepliedAt && (
                <Chip size="small" color="primary" label={t('profile.notes.badgeOwnerReplied')} />
              )}
              {showOwnerReceipt && !note.ownerRepliedAt && note.ownerSeenAt && (
                <Chip size="small" color="info" label={t('profile.notes.badgeSeenByOwner')} />
              )}
              {showOwnerReceipt && !note.ownerRepliedAt && !note.ownerSeenAt && (
                <Chip size="small" color="default" label={t('profile.notes.badgeNotSeenByOwner')} />
              )}
              {isOwnerOrphanedReply && (
                <Chip size="small" color="warning" label={t('profile.notes.badgeOrphanedReply')} />
              )}
              {note.archived && <Chip size="small" color="default" label={t('profile.notes.badgeArchived')} />}
            </Stack>
            {canCollapseThread && (
              <Tooltip title={isThreadOpen ? t('profile.notes.collapseThread') : t('profile.notes.expandThread')}>
                <IconButton
                  size="small"
                  onClick={() => setIsThreadOpen(prev => !prev)}
                  aria-label={isThreadOpen ? t('profile.notes.collapseThread') : t('profile.notes.expandThread')}
                >
                  {isThreadOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {isEditing ? (
          <Stack spacing={1}>
            <InputField
              label={t('profile.notes.edit')}
              value={editDraft}
              onChange={e => onEditDraftChange(e.target.value)}
              minRows={2}
              multiline
              fullWidth
              autoFocus
              insetLabel
            />
            {depth === 0 && (
              <Autocomplete
                multiple
                freeSolo={canCreateTagInline}
                options={availableTags}
                value={availableTags.filter(tag => editTagIds.includes(tag.id))}
                filterOptions={(options, params) => {
                  const filtered = tagFilter(options, params)
                  const trimmed = params.inputValue.trim()
                  const isExisting = options.some(o => o.name.toLowerCase() === trimmed.toLowerCase())
                  if (canCreateTagInline && trimmed && !isExisting) {
                    filtered.push({ id: CREATE_TAG_SENTINEL_ID, name: trimmed } as IProfileNoteTag)
                  }
                  return filtered
                }}
                onChange={async (_event, values) => {
                  const normalized = (values as Array<string | IProfileNoteTag>).map(v =>
                    typeof v === 'object' && v.id === CREATE_TAG_SENTINEL_ID ? v.name : v
                  )
                  const nextTagIds = await onResolveTagIds(normalized)
                  onEditTagIdsChange(nextTagIds)
                }}
                getOptionLabel={option =>
                  typeof option === 'string'
                    ? option
                    : option.id === CREATE_TAG_SENTINEL_ID
                      ? t('profile.notes.createTag', { name: option.name })
                      : option.name
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => {
                  const label =
                    option.id === CREATE_TAG_SENTINEL_ID
                      ? t('profile.notes.createTag', { name: option.name })
                      : option.name

                  return (
                    <li {...props} key={option.id === CREATE_TAG_SENTINEL_ID ? `create-${option.name}` : option.id}>
                      <Chip
                        label={label}
                        size="small"
                        sx={
                          option.id !== CREATE_TAG_SENTINEL_ID && option.color
                            ? { backgroundColor: option.color, color: '#fff' }
                            : undefined
                        }
                      />
                    </li>
                  )
                }}
                renderTags={(value, getTagProps) =>
                  value.map((tag, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={tag.color ? { backgroundColor: tag.color, color: '#fff' } : undefined}
                    />
                  ))
                }
                renderInput={params => (
                  <InputField
                    {...params}
                    name="profile-note-edit-tags"
                    label={t('profile.notes.tags')}
                    insetLabel
                    fullWidth
                    size="small"
                    margin="dense"
                    slotProps={{
                      htmlInput: params?.inputProps,
                      input: params?.InputProps,
                      inputLabel: { shrink: true },
                    }}
                  />
                )}
              />
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={onEditCancel}>
                {t('profile.notes.editCancel')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onEditSave(note)}
                disabled={editDraft.trim().length === 0}
              >
                {t('profile.notes.editSave')}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Typography sx={{ whiteSpace: 'pre-line' }}>{note.message}</Typography>
        )}

        {showOwnerReceipt && note.ownerRepliedAt && (
          <Typography variant="caption" color="text.secondary">
            {t('profile.notes.ownerRepliedAt', { date: convertToDisplayDate(note.ownerRepliedAt) })}
          </Typography>
        )}

        {showOwnerReceipt && !note.ownerRepliedAt && note.ownerSeenAt && (
          <Typography variant="caption" color="text.secondary">
            {t('profile.notes.ownerSeenAt', { date: convertToDisplayDate(note.ownerSeenAt) })}
          </Typography>
        )}

        {isOwnerOrphanedReply && (
          <Typography variant="caption" color="text.secondary">
            {t('profile.notes.orphanedReplyInfo')}
          </Typography>
        )}

        {((canManageNotes && !isOwnerProfile) || isOwnNote || canReply) && !isEditing && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            {isOwnNote && (
              <Button variant="outlined" size="small" onClick={() => onEditStart(note)}>
                {t('profile.notes.edit')}
              </Button>
            )}
            {canManageNotes && !isOwnerProfile && (
              <Button variant="outlined" size="small" onClick={() => onToggleVisibility(note)}>
                {note.visibleToOwner ? t('profile.notes.hideFromOwner') : t('profile.notes.showToOwner')}
              </Button>
            )}
            {canArchiveNotes && !isOwnerProfile && depth === 0 && (
              <Button variant="outlined" size="small" onClick={() => onToggleArchive(note)}>
                {note.archived ? t('profile.notes.unarchive') : t('profile.notes.archive')}
              </Button>
            )}
            {canReply && (
              <Tooltip title={isReplyComposerOpen ? t('profile.notes.editCancel') : t('profile.notes.reply')}>
                <IconButton
                  sx={{ ml: 'auto' }}
                  onClick={() => onReplyComposerToggle(isReplyComposerOpen ? null : note.id)}
                  aria-label={isReplyComposerOpen ? t('profile.notes.editCancel') : t('profile.notes.reply')}
                >
                  {isReplyComposerOpen ? <CloseIcon /> : <ReplyOutlinedIcon />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}

        {canReply && (
          <Stack spacing={1}>
            <Collapse in={isReplyComposerOpen} timeout="auto" unmountOnExit>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <InputField
                  label={t('profile.notes.reply')}
                  value={replyDraftByNote[note.id] ?? ''}
                  onChange={e => onReplyDraftChange(note.id, e.target.value)}
                  minRows={2}
                  multiline
                  fullWidth
                  insetLabel
                />
                {canTagNotes && (
                  <Autocomplete
                    multiple
                    freeSolo={canCreateTagInline}
                    options={availableTags}
                    value={availableTags.filter(tag => (replyTagIdsByNote[note.id] ?? []).includes(tag.id))}
                    filterOptions={(options, params) => {
                      const filtered = tagFilter(options, params)
                      const trimmed = params.inputValue.trim()
                      const isExisting = options.some(o => o.name.toLowerCase() === trimmed.toLowerCase())
                      if (canCreateTagInline && trimmed && !isExisting) {
                        filtered.push({ id: CREATE_TAG_SENTINEL_ID, name: trimmed } as IProfileNoteTag)
                      }
                      return filtered
                    }}
                    onChange={async (_event, values) => {
                      const normalized = (values as Array<string | IProfileNoteTag>).map(v =>
                        typeof v === 'object' && v.id === CREATE_TAG_SENTINEL_ID ? v.name : v
                      )
                      const nextTagIds = await onResolveTagIds(normalized)
                      onReplyTagIdsChange(note.id, nextTagIds)
                    }}
                    getOptionLabel={option =>
                      typeof option === 'string'
                        ? option
                        : option.id === CREATE_TAG_SENTINEL_ID
                          ? t('profile.notes.createTag', { name: option.name })
                          : option.name
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => {
                      const label =
                        option.id === CREATE_TAG_SENTINEL_ID
                          ? t('profile.notes.createTag', { name: option.name })
                          : option.name

                      return (
                        <li {...props} key={option.id === CREATE_TAG_SENTINEL_ID ? `create-${option.name}` : option.id}>
                          <Chip
                            label={label}
                            size="small"
                            sx={
                              option.id !== CREATE_TAG_SENTINEL_ID && option.color
                                ? { backgroundColor: option.color, color: '#fff' }
                                : undefined
                            }
                          />
                        </li>
                      )
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((tag, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={tag.id}
                          label={tag.name}
                          size="small"
                          sx={tag.color ? { backgroundColor: tag.color, color: '#fff' } : undefined}
                        />
                      ))
                    }
                    renderInput={params => (
                      <InputField
                        {...params}
                        name={`profile-note-reply-tags-${note.id}`}
                        label={t('profile.notes.tags')}
                        insetLabel
                        fullWidth
                        size="small"
                        margin="dense"
                        slotProps={{
                          htmlInput: params?.inputProps,
                          input: params?.InputProps,
                          inputLabel: { shrink: true },
                        }}
                      />
                    )}
                  />
                )}
                <Stack direction="row" justifyContent="flex-end">
                  {canManageNotes && !isOwnerProfile && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={replyVisibleToOwnerByNote[note.id] ?? note.visibleToOwner}
                          onChange={e => onReplyVisibilityChange(note.id, e.target.checked)}
                          slotProps={{ input: { 'aria-label': `profile-note-reply-visible-to-owner-${note.id}` } }}
                        />
                      }
                      label={t('profile.notes.visibleToOwner')}
                    />
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      await onReply(note)
                      onReplyComposerToggle(null)
                    }}
                    disabled={(replyDraftByNote[note.id] ?? '').trim().length === 0}
                  >
                    {t('profile.notes.sendReply')}
                  </Button>
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        )}

        <Collapse in={!canCollapseThread || isThreadOpen} timeout="auto" unmountOnExit={canCollapseThread}>
          <Stack spacing={1}>
            {note.replies.map(reply => (
              <NoteItem
                key={reply.id}
                note={reply}
                depth={depth + 1}
                canManageNotes={canManageNotes}
                canArchiveNotes={canArchiveNotes}
                isOwnerProfile={isOwnerProfile}
                currentAccountId={currentAccountId}
                replyDraftByNote={replyDraftByNote}
                replyVisibleToOwnerByNote={replyVisibleToOwnerByNote}
                replyTagIdsByNote={replyTagIdsByNote}
                editingNoteId={editingNoteId}
                editDraft={editDraft}
                onReplyDraftChange={onReplyDraftChange}
                onReplyVisibilityChange={onReplyVisibilityChange}
                onReplyTagIdsChange={onReplyTagIdsChange}
                onReply={onReply}
                onToggleVisibility={onToggleVisibility}
                onToggleArchive={onToggleArchive}
                onEditStart={onEditStart}
                onEditDraftChange={onEditDraftChange}
                availableTags={availableTags}
                canTagNotes={canTagNotes}
                canCreateTagInline={canCreateTagInline}
                editTagIds={editTagIds}
                onEditTagIdsChange={onEditTagIdsChange}
                onResolveTagIds={onResolveTagIds}
                onEditSave={onEditSave}
                onEditCancel={onEditCancel}
                activeReplyComposerNoteId={activeReplyComposerNoteId}
                onReplyComposerToggle={onReplyComposerToggle}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  )
}

export default NoteItem
